<template>
    <div class="my-events">
        <div class="my-events__head">
            <h2 class="my-events__title">{{ langJson('My Events') }}</h2>
            <BTN type="active" bh="48" :load="creating" @handle="createEvent">{{ langJson('New event') }}</BTN>
        </div>
        <div class="my-events__bar">
            <SelBtn class="my-events__tabs" v-model="tab" type="main" size="unselected" :list="tabList" @change="changeTab"></SelBtn>
            <Sel :size="isPhone ? 140 : 200" :left="isPhone ? -140 : 0" :showBorder="true" :showIcon="true" type="select" :list="sortList" :searchHeight="true" @change="changeSort"></Sel>
        </div>

        <div v-if="loading && !events.length" class="my-events__empty">{{ langJson('Loading…') }}</div>
        <div v-else-if="!events.length" class="my-events__empty">
            <nu-data></nu-data>
            <BTN v-if="tab === 'draft'" type="detail" bh="44" @handle="createEvent">{{ langJson('Create your first event') }}</BTN>
        </div>
        <div v-else class="my-events__grid">
            <EventCard v-for="item in events" :key="item.id" :event="item" @open="openEvent" @action="onAction"></EventCard>
        </div>

        <div class="my-events__pager">
            <Page v-if="total > pageSize" v-model="page" :all="total" :size="pageSize" :isBtn="true" @change="load"></Page>
        </div>
    </div>
</template>
<script>
import organizerApi from '@/api/organizer'
import { apiErrorMessage, navigate } from '@/assets/js/organizerEvent'

const TABS = ['draft', 'review', 'live', 'archive']

export default {
    name: 'MyEventsPage',
    data() {
        return {
            tab: TABS.includes(this.$route.query.tab) ? this.$route.query.tab : 'draft',
            sort: 'newest',
            page: 1,
            pageSize: 12,
            total: 0,
            counts: { draft: 0, review: 0, live: 0, archive: 0 },
            events: [],
            loading: false,
            creating: false,
        }
    },
    computed: {
        tabList() {
            const label = { draft: 'Draft', review: 'In review', live: 'Live', archive: 'Archive' }
            return TABS.map((value) => ({ value, lable: `${this.langJson(label[value])} (${this.counts[value]})` }))
        },
        sortList() {
            return [
                { lable: this.langJson('All'), value: '' },
                { lable: this.langJson('New created'), value: 'newest' },
                { lable: this.langJson('Oldest submitted'), value: 'oldest_submitted' },
            ]
        },
    },
    created() {
        // Brand pages show the brand header (Create ▾ instead of My Tickets).
        this.$store.commit('saveHeadView', { create: false })
        this.load()
    },
    methods: {
        async load() {
            this.loading = true
            try {
                const res = await organizerApi.listEvents({ tab: this.tab, sort: this.sort, page: this.page, pageSize: this.pageSize })
                this.events = res.data.items
                this.total = res.data.total
                this.counts = res.data.counts
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not load your events')))
            }
            this.loading = false
        },
        changeTab() {
            this.page = 1
            navigate(this, { query: { ...this.$route.query, tab: this.tab } }, { replace: true })
            this.load()
        },
        changeSort(value) {
            this.sort = value || 'newest'
            this.page = 1
            this.load()
        },
        async createEvent() {
            this.creating = true
            try {
                const res = await organizerApi.createEvent()
                this.$router.push({ path: '/brand/events/edit', query: { id: res.data.id, step: 'basics' } })
            } catch (err) {
                this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not create the event')))
            }
            this.creating = false
        },
        openEvent(event) {
            this.$router.push({ path: '/brand/events/overview', query: { id: event.id } })
        },
        async onAction(action, event) {
            if (action === 'preview') {
                if (event.status === 'live') this.$router.push(`/events/${event.id}`)
                else this.$router.push({ path: '/brand/events/preview', query: { id: event.id } })
            } else if (action === 'edit') {
                this.$router.push({ path: '/brand/events/edit', query: { id: event.id, step: 'basics' } })
            } else if (action === 'overview') {
                this.openEvent(event)
            } else if (action === 'end') {
                try {
                    await this.$confirm(this.langJson('Ticket sales stop straight away; check-in keeps working.'), `${this.langJson('End')} “${event.title}”?`, {
                        confirmButtonText: this.langJson('End event'),
                        cancelButtonText: this.langJson('Cancel'),
                        type: 'warning',
                    })
                } catch (e) {
                    return
                }
                try {
                    await organizerApi.endEvent(event.id)
                    this.pushMsg('success', this.langJson('Event ended — it’s now in Archive'))
                    this.load()
                } catch (err) {
                    this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not end the event')))
                }
            } else if (action === 'delete') {
                try {
                    await this.$confirm(this.langJson('This permanently deletes the draft.'), `${this.langJson('Delete')} “${event.title}”?`, {
                        confirmButtonText: this.langJson('Delete'),
                        cancelButtonText: this.langJson('Cancel'),
                        type: 'warning',
                    })
                } catch (e) {
                    return
                }
                try {
                    await organizerApi.deleteEvent(event.id)
                    this.pushMsg('success', this.langJson('Event deleted'))
                    this.load()
                } catch (err) {
                    this.pushMsg('error', apiErrorMessage(err, this.langJson('Could not delete the event')))
                }
            }
        },
    },
}
</script>
<style lang="scss" scoped>
.my-events {
    width: 100%;
    max-width: var(--max_width);
    margin: 0 auto;
    padding-bottom: 64px;

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 32px;
        gap: 16px;
    }
    &__title {
        font-family: 'Cabinet Grotesk';
        font-weight: 800;
        font-size: 40px;
        line-height: 48px;
        color: var(--font_deep_color);
    }
    &__bar {
        margin-top: 18px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
    }
    &__tabs {
        flex: 1;
        overflow-x: auto;
    }
    &__grid {
        margin-top: 26px;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(335px, 1fr));
        gap: 32px;
    }
    &__empty {
        min-height: 320px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        font-family: 'Lato';
        color: var(--font_modif_color);
    }
    &__pager {
        margin-top: 48px;
    }
}

@media only screen and (max-width: 1080px) {
    .my-events {
        padding-left: 16px;
        padding-right: 16px;
        box-sizing: border-box;

        &__title {
            font-size: 32px;
        }
        &__bar {
            flex-direction: column;
            align-items: flex-end;
        }
        &__tabs {
            width: 100%;
        }
        &__grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 20px;
            margin-top: 12px;
        }
    }
}
</style>
